import React, { useState } from "react";
import { toast } from "sonner";
import {
  useBDAdminKeyStatus, useSaveBDAdminKey,
  useBDUserKeyStatus, useSaveBDUserKey,
  useBDCollectors, useCreateBDCollector, useUpdateBDCollector,
  useDeleteBDCollector, useTestBDCollector, useDiscoverBDCollectors,
  useBDBudget, useSaveBDAdminBudget, useSaveBDUserBudget,
  useResetBDBudget, useRunNowBDCollector,
  useBDMode, useSwitchBDMode,
} from "@/lib/queries";
import {
  Database, Plus, Trash2, Zap, Save, Eye, EyeOff, RefreshCw,
  CheckCircle2, AlertCircle, ChevronDown, ChevronRight, Pencil, X,
  DollarSign, AlertTriangle, PlayCircle, ListChecks, Link as LinkIcon, Radio,
  Users, Server, ArrowRightLeft, Search,
} from "lucide-react";

const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";
const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono";

const EMPTY_FORM = {
  name: "",
  retailer_id: "",
  collector_id: "",
  region: "AU",
  currency: "AUD",
  default_inputs_text: '[\n  {"url": "https://www.bigw.com.au/search?text=pokemon"}\n]',
  interval_seconds: 300,
  enabled: true,
};

function parseInputsOrThrow(text) {
  const v = JSON.parse(text);
  if (!Array.isArray(v)) throw new Error("default_inputs must be a JSON array");
  return v;
}

function CollectorRow({ collector, onTest, onEdit, onDelete, onToggle, onRunNow, testing, monthlyCost }) {
  const [expanded, setExpanded] = useState(false);
  const status = collector.last_error
    ? { color: "#EF4444", icon: <AlertCircle className="w-3 h-3" />, txt: "Last run errored" }
    : collector.last_completed_at
      ? { color: "#22C55E", icon: <CheckCircle2 className="w-3 h-3" />, txt: `${collector.last_count || 0} records · ${new Date(collector.last_completed_at).toLocaleTimeString()}` }
      : { color: "#737373", icon: null, txt: "Never run" };
  return (
    <div data-testid={`bd-collector-${collector.id}`} className={`border rounded-sm ${collector.enabled ? "bg-[#0d0d0d] border-[#262626]" : "bg-[#0a0a0a] border-[#1a1a1a] opacity-60"}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button onClick={() => setExpanded((v) => !v)} className="text-[#737373] hover:text-white shrink-0">
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-white truncate">{collector.name}</span>
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-[#EAB308]/30 text-[#EAB308] rounded-sm">{collector.retailer_id}</span>
            <span className="text-[10px] font-mono text-[#525252]">{collector.collector_id}</span>
            {monthlyCost != null && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 border border-[#3B82F6]/30 text-[#93C5FD] rounded-sm" title="Records this month × cost/1k">
                ${monthlyCost.toFixed(2)} / mo
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono mt-0.5 flex items-center gap-1.5" style={{ color: status.color }}>
            {status.icon}{status.txt}
            {collector.last_error && <span className="text-[10px] text-[#A3A3A3] truncate" title={collector.last_error}>· {collector.last_error.slice(0, 60)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onToggle(collector)}
            className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border rounded-sm ${collector.enabled ? "border-[#22C55E]/40 text-[#22C55E]" : "border-[#525252] text-[#737373]"}`}
            data-testid={`bd-toggle-${collector.id}`}
            title={collector.enabled ? "Disable" : "Enable"}
          >
            {collector.enabled ? "On" : "Off"}
          </button>
          <button
            onClick={() => onRunNow(collector)}
            className="p-1.5 text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm"
            title="Run now — bypass interval throttle on the next scan cycle"
            data-testid={`bd-runnow-${collector.id}`}
          >
            <PlayCircle className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onTest(collector)}
            disabled={testing}
            className="p-1.5 text-[#EAB308] hover:bg-[#EAB308]/10 rounded-sm disabled:opacity-50"
            title="Test (trigger + poll up to 2 min)"
            data-testid={`bd-test-${collector.id}`}
          >
            {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onEdit(collector)} className="p-1.5 text-[#737373] hover:text-[#3B82F6] hover:bg-[#1A1A1A] rounded-sm" title="Edit" data-testid={`bd-edit-${collector.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(collector)} className="p-1.5 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm" title="Delete" data-testid={`bd-delete-${collector.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[#1A1A1A] pt-2 font-mono text-[10px] text-[#A3A3A3] grid grid-cols-2 gap-2">
          <div>
            <div className="text-[#525252] uppercase tracking-wider mb-0.5">Region</div>
            <div className="text-white">{collector.region} · {collector.currency}</div>
          </div>
          <div>
            <div className="text-[#525252] uppercase tracking-wider mb-0.5">Interval</div>
            <div className="text-white">{collector.interval_seconds}s ({Math.round(collector.interval_seconds / 60)} min)</div>
          </div>
          <div className="col-span-2">
            <div className="text-[#525252] uppercase tracking-wider mb-0.5">Inputs sent to collector</div>
            <pre className="bg-[#050505] border border-[#1f1f1f] p-2 rounded-sm text-[#D4D4D4] overflow-auto max-h-32">{JSON.stringify(collector.default_inputs || [], null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function CollectorForm({ scope, onCancel, editing, onSaved }) {
  const create = useCreateBDCollector();
  const update = useUpdateBDCollector();
  const discover = useDiscoverBDCollectors();
  const [form, setForm] = useState(() => {
    if (editing) {
      return {
        ...EMPTY_FORM,
        ...editing,
        default_inputs_text: JSON.stringify(editing.default_inputs || [], null, 2),
      };
    }
    return EMPTY_FORM;
  });
  const [discovered, setDiscovered] = useState(null);  // null | {ok, count, collectors, error}
  const [showDiscovered, setShowDiscovered] = useState(false);

  const runDiscover = async () => {
    try {
      const data = await discover.mutateAsync(scope);
      setDiscovered(data);
      setShowDiscovered(true);
      if (!data.ok) toast.error("Discover failed", { description: data.error?.slice(0, 200) });
      else if (data.count === 0) toast.info("No collectors found on your Bright Data account");
      else toast.success(`Found ${data.count} collector${data.count === 1 ? "" : "s"}`);
    } catch (e) {
      const err = e?.response?.data?.detail || e?.message || "Network error";
      setDiscovered({ ok: false, error: err });
      setShowDiscovered(true);
      toast.error("Discover failed", { description: err });
    }
  };

  const pickDiscovered = (c) => {
    setForm({
      ...form,
      collector_id: c.id,
      name: form.name || c.name || c.id,
    });
    setShowDiscovered(false);
    toast.success(`Picked '${c.name}' (${c.id})`);
  };

  const submit = (e) => {
    e.preventDefault();
    let inputs;
    try {
      inputs = parseInputsOrThrow(form.default_inputs_text);
    } catch (err) {
      toast.error("Invalid JSON in default_inputs", { description: err.message });
      return;
    }
    if (!form.name.trim() || !form.retailer_id.trim() || !form.collector_id.trim()) {
      toast.error("Name, retailer_id and collector_id are required");
      return;
    }
    const body = {
      name: form.name.trim(),
      retailer_id: form.retailer_id.trim(),
      collector_id: form.collector_id.trim(),
      region: form.region || "AU",
      currency: form.currency || "AUD",
      default_inputs: inputs,
      interval_seconds: Number(form.interval_seconds) || 300,
      enabled: !!form.enabled,
    };
    if (editing) {
      update.mutate({ id: editing.id, body }, {
        onSuccess: () => { toast.success("Collector updated"); onSaved(); },
        onError: (e) => toast.error("Update failed", { description: e?.response?.data?.detail }),
      });
    } else {
      create.mutate({ scope, body }, {
        onSuccess: () => { toast.success(`${scope === "admin" ? "Admin" : "User"} collector created`); onSaved(); },
        onError: (e) => toast.error("Create failed", { description: e?.response?.data?.detail }),
      });
    }
  };
  const saving = create.isPending || update.isPending;

  return (
    <form onSubmit={submit} className="bg-[#050505] border border-[#3B82F6]/40 rounded-sm p-3 space-y-3" data-testid={`bd-collector-form-${scope}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#93C5FD]">
          {editing ? "Edit collector" : `New ${scope} collector`}
        </div>
        <button type="button" onClick={onCancel} className="text-[10px] uppercase tracking-wider text-[#737373] hover:text-white flex items-center gap-1">
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className={LABEL}>Name</div>
          <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="BIG W Pokemon search" data-testid="bd-form-name" />
        </div>
        <div>
          <div className={LABEL}>Retailer ID</div>
          <input className={INPUT} value={form.retailer_id} onChange={(e) => setForm({ ...form, retailer_id: e.target.value })} placeholder="bigw, kmart_au, target_au …" data-testid="bd-form-retailer" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className={LABEL.replace(" mb-1.5", "")}>BD Collector ID</div>
            <button
              type="button"
              onClick={runDiscover}
              disabled={discover.isPending}
              className="text-[10px] uppercase tracking-wider font-bold text-[#3B82F6] hover:text-[#93C5FD] flex items-center gap-1 disabled:opacity-50"
              data-testid="bd-form-discover"
              title="Fetch the list of scrapers from your Bright Data account so you can pick instead of pasting"
            >
              {discover.isPending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              {discover.isPending ? "Discovering…" : "Discover my collectors"}
            </button>
          </div>
          <input className={INPUT} value={form.collector_id} onChange={(e) => setForm({ ...form, collector_id: e.target.value })} placeholder="gd_lwqpy81i9pgmf7wcv  (or paste manually)" data-testid="bd-form-collector-id" />
          {showDiscovered && discovered && (
            <div className="mt-2 bg-[#050505] border border-[#3B82F6]/30 rounded-sm p-2 max-h-64 overflow-auto" data-testid="bd-discovered-list">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#93C5FD]">
                  {discovered.ok ? `${discovered.count} scraper${discovered.count === 1 ? "" : "s"} found` : "Discover failed"}
                </div>
                <button type="button" onClick={() => setShowDiscovered(false)} className="text-[#737373] hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
              {discovered.ok ? (
                discovered.count === 0 ? (
                  <div className="text-[11px] text-[#A3A3A3] italic">No collectors found on your BD account. Build one at <a href="https://brightdata.com/cp/scrapers" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] underline">Scraper Studio</a> first.</div>
                ) : (
                  <div className="space-y-1">
                    {discovered.collectors.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickDiscovered(c)}
                        className="w-full text-left p-2 border border-[#1f1f1f] hover:border-[#3B82F6] hover:bg-[#3B82F6]/5 rounded-sm transition-colors flex items-center justify-between gap-2 group"
                        data-testid={`bd-discovered-pick-${c.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-white truncate">{c.name}</div>
                          <div className="text-[10px] font-mono text-[#737373]">{c.id}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {c.status && c.status !== "unknown" && (
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${/ready|live|active|deployed/i.test(c.status) ? "border border-[#22C55E]/40 text-[#22C55E]" : "border border-[#737373]/40 text-[#A3A3A3]"}`}>
                              {c.status}
                            </span>
                          )}
                          <span className="text-[9px] uppercase tracking-wider font-mono text-[#525252]">{c.api_version?.split("-")[0]}</span>
                          <Plus className="w-3 h-3 text-[#3B82F6] opacity-0 group-hover:opacity-100" />
                        </div>
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-[10px] text-[#FCA5A5] leading-relaxed">{discovered.error}</div>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className={LABEL}>Region</div>
            <input className={INPUT} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="AU" />
          </div>
          <div>
            <div className={LABEL}>Currency</div>
            <input className={INPUT} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="AUD" />
          </div>
        </div>
      </div>
      <div>
        <div className={LABEL}>Default inputs (JSON array — sent to BD on every trigger)</div>
        <textarea
          className={INPUT + " min-h-[110px] resize-y"}
          value={form.default_inputs_text}
          onChange={(e) => setForm({ ...form, default_inputs_text: e.target.value })}
          spellCheck={false}
          data-testid="bd-form-inputs"
        />
        <div className="text-[10px] text-[#525252] mt-1">
          Most IDE collectors expect <code className="text-[#A3A3A3]">[{"{"}\"url\": \"...\"{"}"}]</code>. Match whatever input shape your collector declares.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <div>
          <div className={LABEL}>Re-trigger interval (seconds)</div>
          <input type="number" min="60" max="3600" className={INPUT} value={form.interval_seconds}
            onChange={(e) => setForm({ ...form, interval_seconds: Number(e.target.value) })}
            data-testid="bd-form-interval" />
          <div className="text-[10px] text-[#525252] mt-1">Min 60s. BD collectors usually take 30–120s per run.</div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[#EAB308]" data-testid="bd-form-enabled" />
          <span className="text-xs uppercase tracking-wider font-bold text-white">Enabled</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="submit" disabled={saving}
          className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-sm hover:bg-[#FDE047] disabled:opacity-60 flex items-center gap-1.5"
          data-testid="bd-form-save">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : (editing ? "Update" : "Add collector")}
        </button>
      </div>
    </form>
  );
}

function KeyInput({ label, helpText, configured, preview, onSave, saving, dataPrefix }) {
  const [val, setVal] = useState("");
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className={LABEL}>{label}</div>
      {configured && (
        <div className="text-[11px] font-mono text-[#22C55E] mb-1">
          ● Configured: {preview}
        </div>
      )}
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          className={INPUT + " pr-20"}
          placeholder={configured ? "Paste a new key to replace" : "36-character BD API token"}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          data-testid={`${dataPrefix}-input`}
        />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-12 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white" tabIndex={-1}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => { onSave(val); setVal(""); }}
          disabled={!val || saving}
          className="absolute right-1 top-1 bottom-1 px-2 text-[10px] uppercase tracking-wider font-bold bg-[#EAB308] text-black rounded-sm disabled:opacity-40 hover:bg-[#FDE047]"
          data-testid={`${dataPrefix}-save`}
        >
          {saving ? "…" : "Save"}
        </button>
      </div>
      {helpText && <div className="text-[10px] text-[#525252] mt-1">{helpText}</div>}
    </div>
  );
}

function BudgetCard({ scope, usage, onSaveBudget, onReset, saving, resetting }) {
  const [budget, setBudget] = useState(usage?.budget_usd ?? "");
  const [cost, setCost] = useState(usage?.cost_per_1k ?? "");
  React.useEffect(() => {
    if (usage) {
      setBudget(usage.budget_usd);
      setCost(usage.cost_per_1k);
    }
  }, [usage]);
  if (!usage) return null;
  const pct = usage.budget_usd > 0 ? Math.min(100, Math.round((usage.est_usd / usage.budget_usd) * 100)) : 0;
  const danger = pct >= 100;
  const warn = pct >= 80 && !danger;
  const barColor = danger ? "#EF4444" : warn ? "#F59E0B" : "#22C55E";
  return (
    <div className="bg-[#050505] border border-[#262626] rounded-sm p-3 mb-3" data-testid={`bd-budget-${scope}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold">
          <DollarSign className="w-3.5 h-3.5 text-[#EAB308]" />
          <span className="text-white">Monthly budget</span>
          <span className="text-[#525252] font-mono normal-case">resets 1st of {usage.period_month}</span>
        </div>
        {usage.paused && (
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#EF4444] border border-[#EF4444]/40 px-1.5 py-0.5 rounded-sm">
            <AlertTriangle className="w-3 h-3" /> Paused
          </span>
        )}
      </div>
      <div className="h-2 bg-[#0d0d0d] border border-[#1f1f1f] rounded-sm overflow-hidden mb-1.5">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <div className="flex items-center justify-between font-mono text-[11px] mb-3">
        <span className="text-white">${usage.est_usd.toFixed(2)} <span className="text-[#525252]">of</span> ${usage.budget_usd.toFixed(2)}</span>
        <span className="text-[#A3A3A3]">{usage.records.toLocaleString()} records · {(usage.records / 1000).toFixed(1)}k × ${usage.cost_per_1k.toFixed(2)}/1k</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
        <div>
          <div className="text-[9px] uppercase tracking-wider font-bold text-[#737373] mb-1">Budget cap (USD/month)</div>
          <input type="number" min="0" step="10" value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#262626] text-white text-sm px-2 py-1.5 rounded-sm font-mono focus:outline-none focus:border-[#EAB308]"
            data-testid={`bd-budget-${scope}-cap`} />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider font-bold text-[#737373] mb-1">Cost per 1k records (USD)</div>
          <input type="number" min="0" step="0.10" value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#262626] text-white text-sm px-2 py-1.5 rounded-sm font-mono focus:outline-none focus:border-[#EAB308]"
            data-testid={`bd-budget-${scope}-cost`} />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onSaveBudget({
              budget_usd: budget === "" ? null : Number(budget),
              cost_per_1k_records_usd: cost === "" ? null : Number(cost),
            })}
            disabled={saving}
            className="flex-1 bg-[#EAB308] text-black font-bold uppercase tracking-wider text-[10px] px-2 py-1.5 rounded-sm hover:bg-[#FDE047] disabled:opacity-50"
            data-testid={`bd-budget-${scope}-save`}>
            {saving ? "…" : "Save"}
          </button>
          <button
            onClick={onReset}
            disabled={resetting}
            className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#525252] rounded-sm disabled:opacity-50"
            title="Zero the records-this-period counter"
            data-testid={`bd-budget-${scope}-reset`}>
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="text-[10px] text-[#525252] mt-2 leading-relaxed">
        When estimated spend hits the cap, {scope === "admin" ? "all admin" : "your"} collectors are <strong className="text-[#EF4444]">paused</strong> until the period rolls over. Set cap to <span className="font-mono">0</span> to disable the guard (NOT recommended).
      </div>
    </div>
  );
}

function ModeExplainer() {
  const modes = [
    {
      icon: LinkIcon, color: "#22C55E",
      title: "Direct URL Watch",
      latency: "~20s per cycle · sub-10s snipe",
      cost: "~200 KB / check",
      best: "Best for: known PDP URLs (preorders, restock pages, products you've already discovered).",
      desc: "Polls a single product URL directly every scan cycle. Fastest path to auto-buy on a known drop. Routes via Bright Data Scraping Browser if enabled, otherwise local Chromium. Paste URLs into your Scalp profile's 'Direct URL Watch' textarea.",
    },
    {
      icon: Database, color: "#EAB308",
      title: "BD Web Scraper IDE (this section)",
      latency: "60–300s per cycle · ~2 min snipe latency",
      cost: "~$1–3 per 1,000 records",
      best: "Best for: broad discovery — finding NEW products you don't have URLs for yet.",
      desc: "Runs a managed BD collector that scrapes search/category pages on a schedule. BD maintains the markup; you just provide the input URL. When a hot record appears, the bot auto-pins it to your active scalp profile's watched_urls list — so future cycles snipe at sub-second latency.",
    },
    {
      icon: Radio, color: "#3B82F6",
      title: "BD Scraping Browser",
      latency: "On-demand · per scan",
      cost: "~$8.40 / GB bandwidth",
      best: "Best for: per-request bypassing of Akamai/DataDome (fallback when IDE isn't set up).",
      desc: "Remote Chromium with residential IPs and JA3 fingerprinting that the scanner can drive over CDP. Bandwidth-billed — more expensive per page than the IDE for repeated scrapes. Configured in the 'Scraping Provider' card above.",
    },
  ];
  return (
    <div className="border border-[#1A1A1A] bg-[#050505] rounded-sm p-3 mt-3" data-testid="bd-mode-explainer">
      <div className="flex items-center gap-2 mb-2">
        <ListChecks className="w-3.5 h-3.5 text-[#A3A3A3]" />
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white">When to use which</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {modes.map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.title} className="border rounded-sm p-2.5 bg-[#0a0a0a]" style={{ borderColor: `${m.color}33` }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                <div className="text-[12px] font-bold text-white">{m.title}</div>
              </div>
              <div className="text-[10px] font-mono mb-1.5" style={{ color: m.color }}>{m.latency}</div>
              <div className="text-[10px] font-mono text-[#A3A3A3] mb-1.5">{m.cost}</div>
              <div className="text-[10px] text-[#D4D4D4] leading-relaxed mb-1">{m.best}</div>
              <div className="text-[10px] text-[#737373] leading-relaxed">{m.desc}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#525252] mt-2 leading-relaxed border-t border-[#1A1A1A] pt-2">
        <strong className="text-[#22C55E]">Tip:</strong> use both. BD Scraper IDE casts a wide net for new drops; matches automatically get auto-pinned to your active profile&apos;s Direct URL Watch list so the bot can snipe them in under a second on the next cycle. The Scraping Browser is a fallback for ad-hoc scrapes.
      </div>
    </div>
  );
}

function ModeSwitch({ isAdmin }) {
  const { data: mode } = useBDMode();
  const switchMode = useSwitchBDMode();
  const [confirmingByo, setConfirmingByo] = useState(false);
  const [deleteAdmin, setDeleteAdmin] = useState(false);
  if (!mode) return null;
  const isByo = mode.mode === "byo";

  const doSwitch = (target) => {
    if (target === "byo") {
      switchMode.mutate({ mode: "byo", delete_admin_collectors: deleteAdmin }, {
        onSuccess: () => {
          toast.success(deleteAdmin
            ? "Switched to BYO mode — admin collectors deleted"
            : "Switched to BYO mode — admin collectors disabled (not deleted)");
          setConfirmingByo(false);
          setDeleteAdmin(false);
        },
      });
    } else {
      switchMode.mutate({ mode: "hybrid" }, {
        onSuccess: () => toast.success("Switched to hybrid mode — admin budget restored to $500"),
      });
    }
  };

  return (
    <div className="border border-[#262626] bg-[#050505] rounded-sm p-3 mb-3" data-testid="bd-mode-switch">
      <div className="flex items-center gap-3 flex-wrap">
        <ArrowRightLeft className="w-4 h-4 text-[#EAB308] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white">Cost model</span>
            <span
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm border ${isByo ? "border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/5" : "border-[#A855F7]/40 text-[#C084FC] bg-[#A855F7]/5"}`}
              data-testid="bd-mode-pill">
              {isByo ? "BYO · users pay" : "HYBRID · admin pays"}
            </span>
          </div>
          <div className="text-[11px] text-[#A3A3A3] mt-0.5 leading-relaxed">
            {isByo ? (
              <>Every user runs their own Bright Data account. <strong className="text-white">$0 admin cost.</strong>{" "}
                Admin collectors {mode.admin_collectors_total > 0 ? `(${mode.admin_collectors_total} on file, disabled)` : "are off"}. Switch back to Hybrid to share collectors across all users.</>
            ) : (
              <>Admin runs global collectors covered by the $200/mo subscription{" "}
                (current cap ${mode.admin_budget_usd.toFixed(0)}, {mode.admin_collectors_enabled} active).
                Switch to BYO to shift all scraping cost onto users.</>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="shrink-0 flex items-center gap-2">
            {isByo ? (
              <button
                onClick={() => doSwitch("hybrid")}
                disabled={switchMode.isPending}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold bg-[#A855F7] text-white rounded-sm hover:bg-[#C084FC] disabled:opacity-50 flex items-center gap-1.5"
                data-testid="bd-mode-to-hybrid">
                <Server className="w-3 h-3" /> Switch to Hybrid
              </button>
            ) : confirmingByo ? (
              <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[#EF4444]/40 rounded-sm p-2">
                <label className="flex items-center gap-1.5 text-[10px] text-[#FCA5A5] cursor-pointer">
                  <input type="checkbox" checked={deleteAdmin} onChange={(e) => setDeleteAdmin(e.target.checked)} className="accent-[#EF4444]" data-testid="bd-mode-delete-checkbox" />
                  Also delete admin collectors
                </label>
                <button onClick={() => doSwitch("byo")} disabled={switchMode.isPending}
                  className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold bg-[#22C55E] text-black rounded-sm hover:bg-[#4ADE80] disabled:opacity-50"
                  data-testid="bd-mode-confirm-byo">Confirm</button>
                <button onClick={() => { setConfirmingByo(false); setDeleteAdmin(false); }}
                  className="text-[#737373] hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingByo(true)}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold bg-[#22C55E] text-black rounded-sm hover:bg-[#4ADE80] flex items-center gap-1.5"
                data-testid="bd-mode-to-byo">
                <Users className="w-3 h-3" /> Switch to BYO
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function BrightDataCollectorsPanel({ isAdmin }) {
  const { data: list, isLoading } = useBDCollectors();
  const { data: mode } = useBDMode();
  const { data: adminKey } = useBDAdminKeyStatus(isAdmin);
  const { data: userKey } = useBDUserKeyStatus();
  const { data: budget } = useBDBudget();
  const saveAdminKey = useSaveBDAdminKey();
  const saveUserKey = useSaveBDUserKey();
  const saveAdminBudget = useSaveBDAdminBudget();
  const saveUserBudget = useSaveBDUserBudget();
  const resetBudget = useResetBDBudget();
  const runNow = useRunNowBDCollector();
  const update = useUpdateBDCollector();
  const del = useDeleteBDCollector();
  const test = useTestBDCollector();
  const [editing, setEditing] = useState(null);     // collector object being edited
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const onTest = async (c) => {
    setTestingId(c.id);
    setTestResult(null);
    try {
      const data = await test.mutateAsync(c.id);
      setTestResult({ id: c.id, ...data });
      if (data.ok) toast.success(`Test passed — ${data.total_records} records · ${data.mapped_records} mapped`);
      else toast.error("Test failed", { description: data.error });
    } catch (e) {
      setTestResult({ id: c.id, ok: false, error: e?.message || "Network error" });
    }
    setTestingId(null);
  };

  const onToggle = (c) => {
    update.mutate({ id: c.id, body: { enabled: !c.enabled } }, {
      onSuccess: () => toast.success(c.enabled ? "Disabled" : "Enabled"),
    });
  };

  const onDelete = (c) => {
    if (!window.confirm(`Delete collector "${c.name}"?`)) return;
    del.mutate(c.id, { onSuccess: () => toast.success("Collector removed") });
  };

  const onRunNow = (c) => {
    runNow.mutate(c.id, {
      onSuccess: (d) => toast.success(d.message || "Queued"),
      onError: (e) => toast.error("Failed", { description: e?.response?.data?.detail }),
    });
  };

  // Per-collector estimated cost lookup
  const adminCostById = {};
  const userCostById = {};
  (budget?.admin?.collectors || []).forEach((x) => { adminCostById[x.id] = x.est_usd; });
  (budget?.user?.collectors || []).forEach((x) => { userCostById[x.id] = x.est_usd; });

  if (isLoading) return null;
  const adminList = list?.admin_collectors || [];
  const userList = list?.user_collectors || [];
  const userEnabled = !!userKey?.enabled;

  return (
    <section className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm mt-6" data-testid="bd-collectors-panel">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="font-heading text-xl font-black uppercase tracking-tight flex items-center gap-2">
            <Database className="w-5 h-5 text-[#EAB308]" />
            Bright Data Web Scraper IDE
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-2xl">
            Trigger your <strong className="text-white">Scraper Studio</strong> collectors on every scan cycle and ingest the records as Listings.
            Bright Data does the hard part (residential IPs, JA3 fingerprints, retailer markup changes). You point CardRadar at the
            collector ID and the bot polls it on a schedule. Add as many as you like — each one is bound to a retailer for cost-guard routing.
          </p>
        </div>
      </div>

      {/* When-to-use explainer */}
      <ModeExplainer />

      {/* Mode switch (admin sees button; everyone sees current mode pill) */}
      <ModeSwitch isAdmin={isAdmin} />

      {/* ─── Admin / Global section ─── */}
      {isAdmin && mode?.mode !== "byo" && (
        <div className="mt-4 border border-[#A855F7]/30 bg-[#A855F7]/5 rounded-sm p-4" data-testid="bd-admin-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#C084FC]">Admin · Global</div>
              <div className="text-[11px] text-[#A3A3A3]">Runs for every CardRadar user. You pay the BD bill (rolled into the $200/mo).</div>
            </div>
            <button
              onClick={() => { setShowAdminForm(true); setEditing(null); }}
              className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold bg-[#A855F7] text-white rounded-sm hover:bg-[#C084FC] flex items-center gap-1.5"
              data-testid="bd-add-admin-collector">
              <Plus className="w-3.5 h-3.5" /> Add admin collector
            </button>
          </div>

          <BudgetCard
            scope="admin"
            usage={budget?.admin}
            saving={saveAdminBudget.isPending}
            resetting={resetBudget.isPending}
            onSaveBudget={(body) => saveAdminBudget.mutate(body, {
              onSuccess: () => toast.success("Admin budget updated"),
              onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
            })}
            onReset={() => {
              if (!window.confirm("Reset the admin records-this-period counter to zero?")) return;
              resetBudget.mutate("admin", { onSuccess: () => toast.success("Admin counter reset") });
            }}
          />

          <div className="mb-3">
            <KeyInput
              label="Admin Bright Data API token"
              helpText="From Bright Data → Account Settings → API Tokens (36-char hex)."
              configured={!!adminKey?.configured}
              preview={adminKey?.preview || ""}
              saving={saveAdminKey.isPending}
              dataPrefix="bd-admin-key"
              onSave={(v) => saveAdminKey.mutate({ dataset_api_key: v }, {
                onSuccess: () => toast.success("Admin BD key saved"),
                onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
              })}
            />
          </div>
          {showAdminForm && (
            <div className="mb-3">
              <CollectorForm scope="admin" editing={editing} onCancel={() => { setShowAdminForm(false); setEditing(null); }} onSaved={() => { setShowAdminForm(false); setEditing(null); }} />
            </div>
          )}
          <div className="space-y-2">
            {adminList.length === 0 ? (
              <div className="text-[11px] text-[#737373] italic px-1">No admin collectors yet. Add one to enable global broad-discovery scraping for every user.</div>
            ) : adminList.map((c) => (
              <CollectorRow
                key={c.id} collector={c}
                onTest={onTest} onEdit={(c) => { setEditing(c); setShowAdminForm(true); setShowUserForm(false); }}
                onDelete={onDelete} onToggle={onToggle} onRunNow={onRunNow}
                testing={testingId === c.id}
                monthlyCost={adminCostById[c.id]}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── User BD section — PRIMARY path in BYO mode ─── */}
      <div className="mt-4 border border-[#262626] bg-[#0a0a0a] rounded-sm p-4" data-testid="bd-user-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#EAB308]">
              {mode?.mode === "byo" ? "Your Bright Data Account" : "Your collectors · Optional"}
            </div>
            <div className="text-[11px] text-[#A3A3A3]">
              {mode?.mode === "byo"
                ? "Connect your own Bright Data account and add as many collectors as you like — for ANY retailer/domain you want to scrape. You pay BD directly (~$1-3 per 1,000 records)."
                : "Use your own Bright Data account. You pay BD directly. Only fires while you have at least one armed scalp profile."}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="bd-user-enabled">
            <input
              type="checkbox"
              checked={userEnabled}
              onChange={(e) => saveUserKey.mutate({ enabled: e.target.checked }, {
                onSuccess: () => toast.success(e.target.checked ? "Personal BD enabled" : "Personal BD disabled"),
              })}
              className="w-4 h-4 accent-[#EAB308]"
            />
            <span className="text-xs uppercase tracking-wider font-bold text-white">
              {userEnabled ? "On" : "Off"}
            </span>
          </label>
        </div>
        {userEnabled && (
          <>
            <BudgetCard
              scope="user"
              usage={budget?.user}
              saving={saveUserBudget.isPending}
              resetting={resetBudget.isPending}
              onSaveBudget={(body) => saveUserBudget.mutate(body, {
                onSuccess: () => toast.success("Your budget updated"),
                onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
              })}
              onReset={() => {
                if (!window.confirm("Reset your records-this-period counter to zero?")) return;
                resetBudget.mutate("user", { onSuccess: () => toast.success("Counter reset") });
              }}
            />
            <div className="mb-3">
              <KeyInput
                label="Your Bright Data API token"
                helpText="Stored against your CardRadar account. Used only for collectors you create here."
                configured={!!userKey?.configured}
                preview={userKey?.preview || ""}
                saving={saveUserKey.isPending}
                dataPrefix="bd-user-key"
                onSave={(v) => saveUserKey.mutate({ dataset_api_key: v, enabled: true }, {
                  onSuccess: () => toast.success("Your BD key saved"),
                  onError: (e) => toast.error("Save failed", { description: e?.response?.data?.detail }),
                })}
              />
              <div className="text-[10px] text-[#525252] mt-1 leading-relaxed">
                Don&apos;t have one yet? Sign up at{" "}
                <a href="https://brightdata.com" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">brightdata.com</a>
                {" → "}Account Settings → API Tokens. Then build collectors at{" "}
                <a href="https://brightdata.com/cp/scrapers" target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">Scraper Studio</a>
                {" "}for any retailer/domain you want — paste each <span className="font-mono text-[#A3A3A3]">collector_id</span> below.
              </div>
            </div>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="text-[11px] text-[#A3A3A3]">
                <strong className="text-white">{userList.length}</strong> collector{userList.length === 1 ? "" : "s"} configured · add as many as you like, one per retailer or per search/category page.
              </div>
              <button
                onClick={() => { setShowUserForm(true); setEditing(null); setShowAdminForm(false); }}
                className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold bg-[#EAB308] text-black rounded-sm hover:bg-[#FDE047] flex items-center gap-1.5"
                data-testid="bd-add-user-collector">
                <Plus className="w-3.5 h-3.5" /> Add collector
              </button>
            </div>
            {showUserForm && (
              <div className="mb-3">
                <CollectorForm scope="user" editing={editing} onCancel={() => { setShowUserForm(false); setEditing(null); }} onSaved={() => { setShowUserForm(false); setEditing(null); }} />
              </div>
            )}
            <div className="space-y-2">
              {userList.length === 0 ? (
                <div className="text-[11px] text-[#737373] italic px-1">You haven&apos;t added any personal collectors yet.</div>
              ) : userList.map((c) => (
                <CollectorRow
                  key={c.id} collector={c}
                  onTest={onTest} onEdit={(c) => { setEditing(c); setShowUserForm(true); setShowAdminForm(false); }}
                  onDelete={onDelete} onToggle={onToggle} onRunNow={onRunNow}
                  testing={testingId === c.id}
                  monthlyCost={userCostById[c.id]}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ─── Test result preview ─── */}
      {testResult && (
        <div className={`mt-3 p-3 rounded-sm border text-xs ${testResult.ok ? "border-[#22C55E]/40 bg-[#22C55E]/5 text-[#22C55E]" : "border-[#EF4444]/40 bg-[#EF4444]/5 text-[#EF4444]"}`} data-testid="bd-test-result">
          {testResult.ok ? (
            <div>
              <div className="font-bold mb-1 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Trigger + poll succeeded — {testResult.total_records} record(s), {testResult.mapped_records} mappable to Listings.
              </div>
              <pre className="bg-[#050505] border border-[#1f1f1f] p-2 rounded-sm text-[#D4D4D4] max-h-48 overflow-auto">{JSON.stringify(testResult.sample, null, 2)}</pre>
            </div>
          ) : (
            <div className="flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div>{testResult.error}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
